// B2316: the Audit page's server call. `SHOW AUDIT [WHERE f AND f ...]
// [LIMIT n]` lists the audit trail, newest first (web/docs/sql.md § Audit
// trail). The statement goes on the shared WS.
//
// The server applies the grant filter. A superuser or a holder of the AUDIT
// privilege sees all rows. Another user sees only the rows where the user is
// the actor or the target. The server refuses the guest. The page shows that
// refusal, never a fake empty list.
//
// The filter syntax is the one of `parser.rs` `parse_show_audit`:
// `actor`, `target`, `action`, `result` and `node` take `= '<text>'` only;
// `time` takes >=, >, <= or < and an integer of microseconds or an RFC 3339
// UTC text in quotes.

import { wsSql } from './socket.js';

// The columns of `SHOW AUDIT`, in the order of the server
// (`audit.rs` `SHOW_AUDIT_COLUMNS`).
export const AUDIT_COLUMNS = [
  'time', 'time_us', 'node', 'actor', 'action', 'object', 'branch', 'target', 'grantee',
  'rights', 'result', 'reason', 'count', 'client_addr', 'client_name', 'build',
];

// The server limits (`audit.rs` `SHOW_AUDIT_DEFAULT_LIMIT`, `SHOW_AUDIT_MAX_LIMIT`).
export const AUDIT_DEFAULT_LIMIT = 1000;
export const AUDIT_MAX_LIMIT = 10000;

// The values of the `result` column (`audit.rs` `result_text`).
export const AUDIT_RESULTS = ['ok', 'refused'];

// A SQL string literal: single quotes, each embedded quote doubled. The lexer
// has no backslash escape (`token.rs` `read_string`), so a doubled quote is
// the only escape. The value stays one literal token whatever it holds; a
// NUL character is refused because no audit value holds one.
export function auditLit(value) {
  const s = String(value);
  if (s.includes('\u0000')) throw new Error('a filter value cannot hold a NUL character');
  return "'" + s.replace(/'/g, "''") + "'";
}

// The form that `parse_rfc3339_micros` reads: a date, an optional time with
// seconds and up to 6 fraction digits, and an optional `Z`. The server
// refuses an offset such as `+02:00`.
const RFC3339_UTC = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z?)?$/;

// The value of a time filter as SQL: microseconds since the Unix epoch as an
// integer, or an RFC 3339 UTC text as a string literal. A Date becomes its
// UTC text.
export function timeSql(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new Error('the time is not a valid date');
    if (value.getTime() < 0) throw new Error('a time before 1970 is not supported');
    return auditLit(value.toISOString());
  }
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error(`the time ${value} is not a whole number of microseconds`);
    }
    return String(value);
  }
  const s = String(value ?? '').trim();
  if (/^\d+$/.test(s)) return s;
  if (!RFC3339_UTC.test(s)) {
    throw new Error(`the time ${JSON.stringify(s)} is not microseconds or an RFC 3339 UTC time (2026-10-13T12:00:00Z)`);
  }
  return auditLit(s);
}

// The LIMIT as an integer from 0 to AUDIT_MAX_LIMIT, or null for no LIMIT.
function limitValue(limit) {
  if (limit == null || limit === '') return null;
  const s = String(limit).trim();
  if (!/^\d+$/.test(s)) throw new Error(`the limit ${JSON.stringify(s)} is not a whole number`);
  const n = Number(s);
  if (n > AUDIT_MAX_LIMIT) throw new Error(`the limit ${n} is above the maximum of ${AUDIT_MAX_LIMIT} rows`);
  return n;
}

function present(v) {
  return v != null && String(v) !== '';
}

// The text filters, in the order that the statement writes them.
const TEXT_FILTERS = ['actor', 'target', 'action', 'result', 'node'];

// `SHOW AUDIT` with the given filters. `since` is `time >=` and `until` is
// `time <=`. An empty or missing value is no filter.
export function showAuditSql(filters = {}) {
  const f = filters ?? {};
  const where = [];
  for (const name of TEXT_FILTERS) {
    if (!present(f[name])) continue;
    if (name === 'result' && !AUDIT_RESULTS.includes(String(f[name]))) {
      throw new Error(`the result must be ${AUDIT_RESULTS.join(' or ')}, not ${JSON.stringify(String(f[name]))}`);
    }
    where.push(`${name} = ${auditLit(f[name])}`);
  }
  if (present(f.since)) where.push(`time >= ${timeSql(f.since)}`);
  if (present(f.until)) where.push(`time <= ${timeSql(f.until)}`);
  const limit = limitValue(f.limit);
  let sql = 'SHOW AUDIT';
  if (where.length > 0) sql += ` WHERE ${where.join(' AND ')}`;
  if (limit != null) sql += ` LIMIT ${limit}`;
  return sql;
}

// The rows of the answer as objects keyed by column name.
export function parseAudit(r) {
  const cols = r?.columns ?? [];
  return (r?.data ?? []).map((row) => {
    const o = {};
    cols.forEach((c, i) => { o[c] = row[i]; });
    return o;
  });
}

// Build the statement, send it and parse the answer. A bad filter throws
// before anything goes to the server. A server refusal passes on to the
// caller. `send` is for the tests.
export async function listAudit(filters = {}, send = wsSql) {
  const sql = showAuditSql(filters);
  return parseAudit(await send(sql));
}
